import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types';
import { useAuthStore } from '../stores/authStore';
import { tokenStore } from './tokenStore';

// Create axios instance with default config
// Update baseURL to point to your custom Node.js/Express backend
const baseURL = import.meta.env.VITE_API_BASE_URL;

const api: AxiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  withCredentials: true,
  // headers: {
  //   'Content-Type': 'application/json',
  // },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!(config.data instanceof FormData)) {
    // Only set JSON header when not uploading a file
    config.headers["Content-Type"] = "application/json";
  }
  // If FormData, axios sets the correct multipart/form-data automatically
  return config;
});


// Request interceptor
api.interceptors.request.use(
  (config) => {

    const token = useAuthStore.getState().token || localStorage.getItem('auth_token');

    if (token && tokenStore.isTokenExpired()) {
      useAuthStore.getState().logout();
      localStorage.removeItem('auth_token');
      window.location.href = '/';


      return Promise.reject(
        Object.assign(new Error("Session expired. Please sign in again."), {
          status: 401,
          errorCode: "TOKEN_EXPIRED",
        })
      ) as never;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp
    (config as any).metadata = { startTime: new Date() };

    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response time in development
    if (process.env.NODE_ENV === 'development') {
      const endTime = new Date();
      const startTime = (response.config as any).metadata?.startTime;
      if (startTime) {
        const duration = endTime.getTime() - startTime.getTime();
        console.log(`API ${response.config.method?.toUpperCase()} ${response.config.url}: ${duration}ms`);
      }
    }

    return response;
  },
  (error) => {
    // Handle common error scenarios

    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      const authPages = ['/login', '/register', '/forgot-password', '/reset-password'];
      const currentPath = window.location.pathname;
      if (!authPages.includes(currentPath)) {
        // localStorage.removeItem('auth_token');
        // window.location.href = '/login';
      }
    }

    if (error.response?.status === 403) {
      // Handle forbidden access
      console.error('Access forbidden:', error.response.data);
    }

    if (error.response?.status >= 500) {
      // Handle server errors
      console.error('Server error:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// Generic API request wrapper
export const apiRequest = async <T>(
  config: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  try {
    const response = await api.request<ApiResponse<T>>(config);
    return response.data;
  } catch (error: any) {
    // Return standardized error response
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'An unexpected error occurred',
      data: undefined,
    };
  }
};

// HTTP method helpers
export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  return apiRequest<T>({ ...config, method: 'GET', url });
};

export const post = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  return apiRequest<T>({ ...config, method: 'POST', url, data });
};

export const put = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {


  return apiRequest<T>({ ...config, method: 'PUT', url, data });


};

export const patch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  return apiRequest<T>({ ...config, method: 'PATCH', url, data });
};

export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  return apiRequest<T>({ ...config, method: 'DELETE', url });
};

// Export the axios instance for direct use if needed
export default api;

// Types for API client
export interface RequestConfig extends AxiosRequestConfig {
  metadata?: {
    startTime: Date;
  };
}

// Helper function to build query strings
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  return searchParams.toString();
};
